import React from 'react';
import PropTypes from 'prop-types';
import { Field, getIn } from 'formik';
import { startCase } from 'lodash';
import { FormFeedback } from 'reactstrap';
import { getValidity, processValidate } from 'infrastructure/form';
import TextEditorBase from './TextEditorBase';

import './TextEditor.scss';

const renderTextEditor = ({
  field,
  form: { touched = {}, errors = {}, setFieldTouched, setFieldValue },
  onChange,
  ...props
}) => (
  <>
    <TextEditorBase
      {...props}
      initialValue={field.value}
      {...getValidity(getIn(touched, field.name), getIn(errors, field.name))}
      onBlur={() => {
        if (!getIn(touched, field.name)) setFieldTouched(field.name, true);
      }}
      onChange={value => {
        setFieldValue(field.name, value);
        if (onChange) onChange(value);
      }}
    />
    {getIn(touched, field.name) && getIn(errors, field.name) && (
      <FormFeedback className="validation form__form-group-error" tag="small">
        {getIn(errors, field.name)}
      </FormFeedback>
    )}
  </>
);

renderTextEditor.propTypes = {
  field: PropTypes.shape().isRequired,
  form: PropTypes.shape().isRequired,
  onChange: PropTypes.func,
};

renderTextEditor.defaultProps = {
  onChange: null,
};

/** Rich Markdown text editor<br />
 * [See this comment for toolbar options.](https://github.com/jpuri/react-draft-wysiwyg/issues/4#issuecomment-256276114)
 */

const requiredValidator = ({ field }, value) =>
  value && value !== '<p></p>\n' ? null : `${field} is required`;

const TextEditor = props => {
  const { validate, label, name, toolbarOptions, required } = props;

  const validators = validate;
  if (required) validators.push(requiredValidator);

  return (
    <Field
      {...props}
      name={name}
      validate={processValidate(label || startCase(name), validators, props)}
      render={p =>
        renderTextEditor({
          ...props,
          ...p,
          toolbarOptions,
        })
      }
    />
  );
};

TextEditor.propTypes = {
  /** Form row label */
  label: PropTypes.string,
  /** Input name & ID */
  name: PropTypes.string,
  /** Is form field required? */
  required: PropTypes.bool,
  /** onChange function handler */
  onChange: PropTypes.func,
  /** Toolbar options
      [See this comment for toolbar options.](https://github.com/jpuri/react-draft-wysiwyg/issues/4#issuecomment-256276114)
  */
  toolbarOptions: PropTypes.shape({}),
  /** Validation array */
  validate: PropTypes.arrayOf(PropTypes.shape({})),
};

TextEditor.defaultProps = {
  label: '',
  name: '',
  onChange: null,
  required: false,
  toolbarOptions: {
    options: ['blockType', 'inline', 'list', 'textAlign', 'link'],
    list: {
      options: ['unordered', 'ordered'],
    },
    inline: {
      options: ['bold', 'italic', 'underline'],
    },
  },
  validate: [],
};

export default TextEditor;
